import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDateForInput } from 'utilities/common';
import { DRAWER_WIDTH_OPEN } from 'styles/theme';
import Log from 'utilities/log';
import {
  getAcumaticaClientRequestPreview,
  getAcumaticaPushClientChanges

} from '../clients.actions';
// Adjust these paths to wherever they live in your app:
import { showToast } from 'modules/layout/layout.actions';
import {
  TOAST_MESSAGE_SEVERITY_ERROR,
  TOAST_MESSAGE_SEVERITY_SUCCESS,
} from 'modules/layout/types';
import { GET_ACUMATICA_CLIENT_REQUEST_PREVIEW_FAILURE, GET_ACUMATICA_PUSH_CLIENT_CHANGES_FAILURE } from '../types';

const styles = (theme) => ({
  container: {
    height: 'auto',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: `calc(100% - ${DRAWER_WIDTH_OPEN})`,
  },
  headerFixed: {
    zIndex: 4,
  },
});

const labelSx = { color: '#777667', fontWeight: 400, fontSize: '1rem', textAlign: 'center' };
const valueSx = { color: '#43423a', fontWeight: 500, fontSize: '1rem', textAlign: 'center' };

const HeaderItem = ({ label, value }) => (
  <Box sx={{ marginRight: '20px' }}>
    <Typography variant="subtitle2" sx={{ ...labelSx, marginBottom: '4px' }}>{label}</Typography>
    <Typography variant="body2" sx={valueSx}>{value}</Typography>
  </Box>
);

const PreviewGroup = ({ group }) => (
  <Box sx={{ marginBottom: 3 }}>
    <Typography variant="h6" sx={{ fontSize: '1.05rem', marginBottom: 1 }}>
      {group.riotEntityName} → {group.acumaticaEntityName}
    </Typography>
    {group.acumaticaRequestPreviews?.length ? (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '30%' }}>RIOT Field</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '30%' }}>Acumatica Field</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Value to Send</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {group.acumaticaRequestPreviews.map((row, i) => (
              <TableRow key={`${row.acumaticaFieldDisplayname}-${i}`} hover>
                <TableCell>{row.riotFieldDisplayName}</TableCell>
                <TableCell>{row.acumaticaFieldDisplayname}</TableCell>
                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  {row.acumaticaNewValue === '' ? <em>(empty)</em> : row.acumaticaNewValue}
                </TableCell>
                <TableCell>
                  {row.newValueIsDefault && <Chip label="Default" size="small" color="info" variant="outlined" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Typography variant="body2" color="text.secondary">No fields will be sent for this entity.</Typography>
    )}
  </Box>
);

const ClientHeader = ({
  classes,
  headerInfo,
  shouldShowSideDrawer,
  clientRecordId,
  setReloadData,
  getAcumaticaClientRequestPreview,
  getAcumaticaPushClientChanges,
  showToast,
}) => {
  Log.trace('RENDER', 'ClientHeader');

  const [snapshot, setSnapshot] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupings = snapshot?.previewGroupings ?? [];
  const isOpen = Boolean(snapshot);

  const clearPreview = () => setSnapshot(null);

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const resp = await getAcumaticaClientRequestPreview(clientRecordId);
      if (resp.type === GET_ACUMATICA_CLIENT_REQUEST_PREVIEW_FAILURE) {
        showToast('Failed to create Acumatica preview, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        return;
      }
      setSnapshot(resp.response);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const confirmPush = async () => {
    if (isSubmitting || !snapshot?.screenshotId) return;
    setIsSubmitting(true);
    try {
      const resp = await getAcumaticaPushClientChanges(clientRecordId, snapshot.screenshotId);
      if (resp.type === GET_ACUMATICA_PUSH_CLIENT_CHANGES_FAILURE) {
        showToast('Failed to push changes to Acumatica. The preview may have expired, please regenerate it.', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR,
        });
        return;
      }
      showToast('Changes pushed to Acumatica successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      clearPreview();
      setReloadData(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Paper
        square
        className={classNames('position-fixed', classes.headerFixed, classes.container, {
          [classes.drawerOpen]: shouldShowSideDrawer,
        })}
        sx={{ padding: '10px', zIndex: 4, position: 'relative', width: '100%' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
          {/* Left: action button */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              size="small"
              onClick={loadPreview}
              disabled={isLoadingPreview || isSubmitting}
              startIcon={isLoadingPreview ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isLoadingPreview ? 'Loading...' : 'Sync to Acumatica'}
            </Button>
          </Box>

          {/* Center: header info */}
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <HeaderItem label="Client Number" value={headerInfo.clientCode} />
            <HeaderItem label="Client Name" value={headerInfo.description} />
            <HeaderItem label="Contract Date" value={formatDateForInput(headerInfo.clientContract)} />
            <HeaderItem label="Status" value={headerInfo.clientStatus} />
            <HeaderItem label="Acumatica Id" value={headerInfo.acumaticaStatus} />
          </Box>

          {/* Right: spacer keeps the info centered */}
          <Box sx={{ flex: 1 }} />
        </Box>
      </Paper>

      <Dialog
        open={isOpen}
        onClose={isSubmitting ? undefined : clearPreview}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Review Acumatica Request</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
            These are the values that will be sent to Acumatica. Review them and click Confirm to push.
          </Typography>
          {groupings.length ? (
            groupings.map((group, i) => (
              <PreviewGroup key={`${group.riotEntityName}-${i}`} group={group} />
            ))
          ) : (
            <Typography variant="body2">There is nothing to send.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={clearPreview} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPush}
            disabled={isSubmitting || !groupings.length}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmitting ? 'Pushing...' : 'Confirm & Push'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ClientHeader.propTypes = {
  classes: PropTypes.object.isRequired,
  headerInfo: PropTypes.object.isRequired,
  shouldShowSideDrawer: PropTypes.bool.isRequired,
  clientRecordId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setReloadData: PropTypes.func.isRequired,
  getAcumaticaClientRequestPreview: PropTypes.func.isRequired,
  getAcumaticaPushClientChanges: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  shouldShowSideDrawer: state.layout.shouldShowSideDrawer,
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    getAcumaticaClientRequestPreview,
    getAcumaticaPushClientChanges,
    showToast,
  }),
)(ClientHeader);